import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Contact } from "../models/Contact";
import axios from "../utils/axios";
import { base64ToUint8Array, decryptMessage } from "../services/crypto";
import { parseMessage } from "../utils/messageUtils";
import { combineAndDecryptPrivateKey, decryptString } from "../utils/security";

interface ChatListState {
    contacts: Contact[];
    filteredContacts: Contact[];
    selectedContact: Contact | null;
    error: { message: string; code: string };
    loading: boolean;
    searchQuery: string;
}

const initialState: ChatListState = {
    contacts: [],
    filteredContacts: [],
    selectedContact: null,
    error: { message: "", code: "" },
    loading: false,
    searchQuery: "",
};

export const fetchMessageContacts = createAsyncThunk(
  "contacts/fetchMessageContacts",
  async (_, { rejectWithValue,getState }) => {
    try {
      const response = await axios.get(
        "api/services/app/Contact/GetMessageContacts"
      );

      const state = getState() as { auth: { a: string; b: string } };
      const data: Contact[] = response.data.result;
      

      // Process all contacts in parallel
      const contactsPromises = data.map(async (contact: any) => {
        try {
          const savedValue0 = sessionStorage.getItem("a") || state.auth.a;
          const savedValue1 = sessionStorage.getItem("b") || state.auth.b;          
          
          if (!savedValue0 || !savedValue1) {
            console.error("Missing session storage values for decryption");
            return null;
          }
          
          const value = combineAndDecryptPrivateKey([
            decryptString(savedValue0),
            decryptString(savedValue1),
          ]);          

          const decryptedMessage = await decryptMessage(
            base64ToUint8Array(contact.publicKey || ""),
            base64ToUint8Array(value),
            { ciphertext: contact.lastMessageText, nonce: contact.lastMessageNonce }
          );        
         
          const parts = parseMessage(decryptedMessage);       

          return {
            id: contact.id,
            name: contact.name,
            email: contact.email,
            walletAddress: contact.walletAddress,
            phone: contact.phone,
            lastMessage: parts.length > 0 ? parts[0].content : "",
            lastMessageTime: contact.lastMessageTime,
            unreadCount: contact.unreadCount,
            lastOnlineTime: contact.lastOnlineTime,
            lastMessageNonce: contact.lastMessageNonce,
            lastMessageText: contact.lastMessageText,
            // Add required Contact fields with fallback/defaults if missing
            timestamp: contact.timestamp ?? contact.lastMessageTime ?? Date.now(),
            unread: contact.unread ?? contact.unreadCount ?? 0,
            userId: contact.userId ?? "",
            emailAddress: contact.emailAddress ?? ""
          };
        } catch (error) {
          console.error("Error mapping contact:", error);
          return {
            id: contact.id,
            name: contact.name,
            email: contact.email,
            walletAddress: contact.walletAddress,
            phone: contact.phone,
            lastMessage: "",
            lastMessageTime: contact.lastMessageTime,
            unreadCount: contact.unreadCount,
            lastOnlineTime: contact.lastOnlineTime,
            lastMessageNonce: contact.lastMessageNonce,
            lastMessageText: contact.lastMessageText,
            // Add required Contact fields with fallback/defaults if missing
            timestamp: contact.timestamp ?? contact.lastMessageTime ?? Date.now(),
            unread: contact.unread ?? contact.unreadCount ?? 0,
            userId: contact.userId ?? "",
            emailAddress: contact.emailAddress ?? ""
          };
        }
      });

      // Wait for all promises to resolve and filter out any null values
      const contacts = (await Promise.all(contactsPromises)).filter((c) => c !== null) as Contact[];
      return contacts;
      
    } catch (error: any) {
      return rejectWithValue({
        message: "Failed to fetch message contacts",
        code: "FETCH_MESSAGE_CONTACTS_FAILED",
      });
    }
  }
);

const chatListSlice = createSlice({
    name: 'chatList',
    initialState,
    reducers: {
        setContacts: (state: ChatListState, action: { payload: { contacts: Contact[] } }) => {
            state.contacts = action.payload.contacts;
            // Update filtered contacts whenever contacts change
            state.filteredContacts = filterContacts(state.contacts, state.searchQuery);
        },
        addContact: (state: ChatListState, action: { payload: { contact: Contact } }) => {
            state.contacts.push(action.payload.contact);
            state.filteredContacts = filterContacts(state.contacts, state.searchQuery);
        },
        removeContact: (state: ChatListState, action: { payload: { id: string } }) => {
            state.contacts = state.contacts.filter(contact => contact.id !== action.payload.id);
            state.filteredContacts = filterContacts(state.contacts, state.searchQuery);
        },
        selectContact: (state: ChatListState, action: { payload: { contact: Contact } }) => {
            state.selectedContact = action.payload.contact;
        },
        clearSelectedContact: (state: ChatListState) => {
            state.selectedContact = null;
        },
        setSearchQuery: (state: ChatListState, action: { payload: { query: string } }) => {
            state.searchQuery = action.payload.query;
            state.filteredContacts = filterContacts(state.contacts, state.searchQuery);
        },
        updateContactLastMessage: (state: ChatListState, action: { 
            payload: { 
                contactId: string; 
                lastMessage: string; 
                lastSender?: string;
                timestamp?: string;
            } 
        }) => {
            const { contactId, lastMessage, lastSender, timestamp } = action.payload;
            
            state.contacts = state.contacts.map(contact => {
                if (contact.id === contactId) {
                    return {
                        ...contact,
                        lastMessage,
                        lastSender,
                        timestamp: timestamp || new Date().toISOString()
                    };
                }
                return contact;
            });
            
            state.filteredContacts = filterContacts(state.contacts, state.searchQuery);
            
            // Also update selected contact if it's the one being updated
            if (state.selectedContact?.id === contactId) {
                state.selectedContact = {
                    ...state.selectedContact,
                    lastMessage,
                    lastSender,
                    timestamp: timestamp || new Date().toISOString()
                };
            }
        },
        incrementUnreadCount: (state: ChatListState, action: { payload: { contactId: string } }) => {
            const { contactId } = action.payload;
            
            state.contacts = state.contacts.map(contact => {
                if (contact.id === contactId) {
                    return {
                        ...contact,
                        unread: (contact.unread || 0) + 1
                    };
                }
                return contact;
            });
            
            state.filteredContacts = filterContacts(state.contacts, state.searchQuery);
        },
        resetUnreadCount: (state: ChatListState, action: { payload: { contactId: string } }) => {
            const { contactId } = action.payload;
            
            state.contacts = state.contacts.map(contact => {
                if (contact.id === contactId) {
                    return {
                        ...contact,
                        unread: 0
                    };
                }
                return contact;
            });
            
            state.filteredContacts = filterContacts(state.contacts, state.searchQuery);
            
            // Also reset unread for selected contact if it's the one being updated
            if (state.selectedContact?.id === contactId) {
                state.selectedContact = {
                    ...state.selectedContact,
                    unread: 0
                };
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMessageContacts.pending, (state) => {
                state.error = { message: "", code: "" };
                state.loading = true;
            })
            .addCase(fetchMessageContacts.fulfilled, (state, action: PayloadAction<Contact[]>) => {
                state.contacts = action.payload;
                state.filteredContacts = filterContacts(action.payload, state.searchQuery);
                state.loading = false;
            })
            .addCase(fetchMessageContacts.rejected, (state, action) => {
                state.loading = false;
            });
    }
});

// Helper function to filter contacts
function filterContacts(contacts: Contact[], searchQuery: string): Contact[] {
    if (!searchQuery.trim()) {
        return [...contacts];
    }
    
    const query = searchQuery.toLowerCase();
    return contacts.filter(contact => 
        contact.name.toLowerCase().includes(query) ||
        contact.lastMessage?.toLowerCase().includes(query)
    );
}

export const { 
    setContacts, 
    addContact, 
    removeContact, 
    selectContact, 
    clearSelectedContact,
    setSearchQuery,
    updateContactLastMessage,
    incrementUnreadCount,
    resetUnreadCount
} = chatListSlice.actions;
export default chatListSlice.reducer;